import React, { createContext, useContext } from "react";
import { User } from "../../../../redux/authSlice";
import { Park } from "../../../../type/entities/park-type";
import useParks from "../../../../hooks/api-calls/useParks";
import useUsers from "../../../../hooks/api-calls/useUsers";
import useCompanies from "../../../../hooks/api-calls/useCompanies";
import { usePrivilege } from "../../../../components/priviledge/PriviledgeProvider";
import useCensusStatus from "../../../../hooks/api-calls/useCensusStatus";
import useWindTurbine from "../../../../hooks/api-calls/useWindTurbine";
import useSpecies from "../../../../hooks/api-calls/useSpecies";

interface CensusFormProviderProps {
    children: React.ReactNode;
    speciesFilter?: 'census' | 'ofc';
}

interface CensusFormContextProps {
    usersList: User[];
    parksList: Park[];
    statusList: any[];
    turbinesList: any[];
    speciesList: any[];
    companyList: any[];
    updateWindTurbineHookFilters: (filters: any) => void;
}

const CensusFormContext = createContext<CensusFormContextProps | undefined>(undefined);

export const CensusFormProvider: React.FC<CensusFormProviderProps> = ({
    children,
    speciesFilter = 'census',
}) => {

    const speciesFilters =
        speciesFilter === 'ofc'
            ? { ofcAvailable: true }
            : { censusAvailable: true };

    const { getParksList } = useParks();
    const { getUsersList } = useUsers();
    const { getCompaniesList } = useCompanies();
    const { userCan } = usePrivilege();
    const { getCensusStatusList } = useCensusStatus();
    const { getWindTurbinesList, updateWindTurbineHookFilters } = useWindTurbine();
    const { getSpeciesListScientific } = useSpecies(speciesFilters);

    const usersList = getUsersList();
    const parksList = getParksList();
    const statusList = getCensusStatusList();
    const turbinesList = getWindTurbinesList();
    const speciesList = getSpeciesListScientific();
    const companyList = userCan('list', 'companies') ? getCompaniesList() : [];

    const contextValue: CensusFormContextProps = {
        usersList,
        parksList,
        statusList,
        turbinesList,
        speciesList,
        companyList,
        updateWindTurbineHookFilters,
    };

    return (
        <CensusFormContext.Provider value={contextValue}>
            {children}
        </CensusFormContext.Provider>
     );
};

export const useCensusFormContext = (): CensusFormContextProps => {
    const context = useContext(CensusFormContext);
    if (!context) {
        throw new Error('useCensusFormContext must be used within a CensusFormProvider');
    }
    return context;
};
